import { ref, computed, watch } from "vue";
import { ProductIds } from "@/config/constants";
import { useStore } from "vuex";
import {
  TimelineMilestoneIds,
  titleDictionary,
  type TimelineMilestone,
} from "@/models/applicationStatus";
import useApplicationData from "@/composables/useApplicationData";
import useLoanData from "@/composables/loan/useLoanData";
import { useRedirectStore } from "@/stores/redirect";
import { storeToRefs } from "pinia";

export default function useApplicationStatus() {
  const store = useStore();
  const redirectStore = useRedirectStore();
  const { isRedirecting } = storeToRefs(redirectStore);

  const { emailAddress, hrefTFN, tollFreeNumber } = useApplicationData();
  const { loan } = useLoanData();
  const currentstep = ref<number>(1);

  const slcRedirectUrl = computed<string>(() => store.getters["slcRedirectUrl"]);

  const timeline = computed(() => {
    return store.state.applicationStatusModule.timeline;
  });

  const stepCount = computed<number | undefined>(() => timeline.value.length || undefined);

  const currentActiveStep = computed<number>(() => {
    return timeline.value.find((s: TimelineMilestone) => s.isActive)?.step || 0;
  });
  const currentActiveStepDetails = computed<TimelineMilestone | undefined>(() => {
    return timeline.value.find((s: TimelineMilestone) => s.isActive);
  });
  const selectedStep = computed<TimelineMilestone>(() => {
    return timeline.value.find((s: TimelineMilestone) => s.step === currentstep.value) || {};
  });

  const applicationStatusTypeId = computed<number>(
    () => store.state.appData.application.applicationStatusId,
  );

  const productTypeId = computed<number>(() => store.state.appData.application.productTypeId);

  const useV2 = computed<boolean>(() => {
    const includedProducts = [ProductIds.PEL, ProductIds.LOC];
    return includedProducts.includes(productTypeId.value);
  });

  const allowEditing = ref<boolean | undefined>(undefined);

  const lenderUrl = computed<string>(() => {
    return store.state.lenderData && store.state.lenderData.websiteUrl
      ? store.state.lenderData.websiteUrl
      : "https://www.cuselect.com/";
  });

  const emailAddressLinkHtml = computed<string>(() => {
    return ` <a class="inline-link" aria-description="link to email support team" :href="\`mailto:${emailAddress.value}\`">here</a>`;
  });

  const showSLCButtonFundsDisbursedStep = computed<boolean>(() => {
    if (
      timeline.value.find((s: TimelineMilestone) => s.isActive)?.title ===
        titleDictionary[TimelineMilestoneIds.FUNDS_DISBURSED] &&
      selectedStep.value?.title === titleDictionary[TimelineMilestoneIds.FUNDS_DISBURSED] &&
      loan.value.loanBoarded
    ) {
      return true;
    }

    return false;
  });

  const showViewCertificationUpdates = computed<boolean>(() => {
    if (
      timeline.value.find((s: TimelineMilestone) => s.isActive)?.title ===
        titleDictionary[TimelineMilestoneIds.SCHOOL_CERTIFICATION] &&
      selectedStep.value?.title === titleDictionary[TimelineMilestoneIds.SCHOOL_CERTIFICATION]
    ) {
      return true;
    }

    return false;
  });

  function setCurrentStep(step: number) {
    currentstep.value = step;
    store.dispatch("applicationStatusModule/setSelectedStep", step);
  }

  function redirectToSLC(options: { openInNewTab: boolean }) {
    if (!slcRedirectUrl.value) {
      console.error("SLC redirect url not configured");
      throw new Error("SLC redirect url not configured");
    }
    if (options?.openInNewTab) {
      window.open(`${slcRedirectUrl.value}`, "_blank");
    } else {
      window.location.replace(`${slcRedirectUrl.value}`);
    }

    isRedirecting.value = false;
  }

  watch(
    () => store.state.applicationStatusModule.selectedStep,
    newValue => {
      setCurrentStep(newValue);
    },
    {
      immediate: true,
    },
  );

  watch(
    () => currentActiveStep.value,
    () => {
      setCurrentStep(currentActiveStep.value);
    },
  );

  return {
    allowEditing,
    applicationStatusTypeId,
    currentActiveStep,
    currentActiveStepDetails,
    currentstep,
    emailAddress,
    emailAddressLinkHtml,
    hrefTFN,
    isRedirecting,
    lenderUrl,
    loan,
    productTypeId,
    redirectToSLC,
    selectedStep,
    setCurrentStep,
    showSLCButtonFundsDisbursedStep,
    showViewCertificationUpdates,
    slcRedirectUrl,
    stepCount,
    timeline,
    tollFreeNumber,
    useV2,
  };
}
